import { platform } from '../platform';

// BattleSuite account + API access. The signed-in identity ({ uid, orgId }) is persisted
// separately from the auth session so the app can open its local store and run with no
// signal on a cold start — the token refresh happens in the background and only sync needs it.
// The SDK (vendor/battlesuite-sdk) arrives through the platform adapter and is set up on first use.
const IDENTITY_KEY = 'ops.identity.v1';
const DEVICE_KEY = 'ops.deviceId.v1';

let sdk = null;   // { auth, functions }
function api() {
  if (sdk) return sdk;
  const p = platform();
  const app = p.sdk.initializeApp({ apiBaseUrl: p.apiBaseUrl });
  sdk = { auth: p.sdk.initAuth(app), functions: p.sdk.getFunctions(app) };
  return sdk;
}
const kv = () => platform().kv;

export const getApiBaseUrl = () => platform().apiBaseUrl;

// Sync needs to tell "the server said no" (has an HTTP-ish status) from "no signal" (none).
const STATUS = { unauthenticated: 401, 'permission-denied': 403, 'not-found': 404, 'invalid-argument': 400, 'failed-precondition': 400, aborted: 409, 'resource-exhausted': 429, internal: 500, unknown: 500, unimplemented: 501 };

export async function call(name, data, { timeout = 30000 } = {}) {
  try {
    const res = await platform().sdk.httpsCallable(api().functions, name, { timeout })(data);
    return res.data;
  } catch (e) {
    const code = String(e?.code || '').replace('functions/', '');
    if (STATUS[code]) e.status = STATUS[code];
    const msg = String(e?.message || '');
    // Employment ended (people.md P5: the API's ACCESS_ENDED gate) or the account was switched off → the phone wipes.
    if (msg.includes('ACCESS_ENDED') || msg.includes('Account is not active')) accessEnded.forEach((fn) => fn(msg));
    else if (msg.includes('NO_APP_ACCESS:ops')) accessLost.forEach((fn) => fn());
    throw e;
  }
}

// Transport for @battleops/sync. `refresh` is what the engine calls once on a 401.
export const transport = {
  call,
  refresh: async () => { const u = api().auth.currentUser; if (u) await u.getIdToken(true); },
};

export async function loadIdentity() {
  try { const raw = await kv().getItem(IDENTITY_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

export async function getDeviceId() {
  let id = await kv().getItem(DEVICE_KEY);
  if (!id) {
    id = `${platform().os}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    await kv().setItem(DEVICE_KEY, id);
  }
  return id;
}

// One suite account, access per app: `apps` missing = every app, an admin has every app.
// Mirrors services/api/src/server/apps.ts, which enforces it on the server.
export const hasOpsApp = (profile) => profile?.role === 'admin' || !Array.isArray(profile?.apps) || profile.apps.includes('ops');
const NO_APP = 'This account doesn’t have BattleOps. Ask your admin to turn it on under Team → Apps in BattleBooks.';
export const noAppMessage = NO_APP;

// Fires when the server says this account no longer has BattleOps (an admin took it away).
const accessLost = new Set();
export const onAppAccessLost = (fn) => { accessLost.add(fn); return () => accessLost.delete(fn); };

// Fires when the server says this person's access to the team has ended (endEmployment) or the account is inactive.
// Unlike losing the app, the org's data must leave the device (SYNC.md §3.9): SessionContext wipes the store.
const accessEnded = new Set();
export const onAccessEnded = (fn) => { accessEnded.add(fn); return () => accessEnded.delete(fn); };
export const accessEndedMessage = 'Your access to this team has ended. The team’s data has been removed from this device. Your payslips and T4s stay in BattleBooks — sign in at app.battlebooks.ca.';

// Needs signal once: the org comes from the suite's user directory.
export async function signIn(email, password) {
  const cred = await platform().sdk.signInWithEmailAndPassword(api().auth, email.trim(), password);
  const uid = cred.user.uid;
  const res = await call('__dbGet', { paths: [`users/${uid}`] });
  const profile = res?.docs?.[0]?.data || null;
  const orgId = profile?.activeOrgId || profile?.orgId || null;
  if (!orgId) {
    await platform().sdk.signOut(api().auth);
    const err = new Error('This account is not attached to a BattleBooks organization yet.');
    err.code = 'ops/no-org';
    throw err;
  }
  if (profile?.active === false) {   // employment ended / account switched off — say so, don't open a store
    await platform().sdk.signOut(api().auth);
    const err = new Error(profile?.employment?.pastAccess === 'payslips' ? accessEndedMessage : 'This account is not active. Ask your admin.');
    err.code = 'ops/ended';
    throw err;
  }
  if (!hasOpsApp(profile)) {
    await platform().sdk.signOut(api().auth);
    const err = new Error(NO_APP);
    err.code = 'ops/no-app';
    throw err;
  }
  const identity = { uid, orgId, email: cred.user.email || email.trim(), name: profile?.name || cred.user.displayName || '', role: profile?.role || null, access: accessOf(profile), avatarPath: profile?.avatarPath || null };
  await kv().setItem(IDENTITY_KEY, JSON.stringify(identity));
  return identity;
}

// The member's BattleOps grants (functions/src/access.ts); null = everything. The phone only uses
// them to hide what the server would refuse (shared/access.js).
function accessOf(profile) {
  if (!profile || profile.role === 'admin') return null;
  return profile.access && typeof profile.access === 'object' ? profile.access : null;
}

/** Re-read the profile (role, name, grants) — an admin may have changed them. Needs signal; returns the identity, updated. */
export async function refreshProfile(identity) {
  if (!identity?.uid) return identity;
  const res = await call('__dbGet', { paths: [`users/${identity.uid}`] });
  const p = res?.docs?.[0]?.data;
  if (!p) return identity;
  const next = { ...identity, name: p.name || identity.name, role: p.role || null, access: accessOf(p), avatarPath: p.avatarPath || null };
  if (JSON.stringify(next) === JSON.stringify(identity)) return identity;
  await kv().setItem(IDENTITY_KEY, JSON.stringify(next));
  return next;
}

export async function signOut() {
  await kv().removeItem(IDENTITY_KEY);
  await platform().sdk.signOut(api().auth).catch(() => {});
}

export const resetPassword = (email) => platform().sdk.sendPasswordResetEmail(api().auth, email.trim());

// Fires with null when the server revokes the session (refresh → 401).
export const onSessionLost = (fn) => {
  let seenUser = false;
  return platform().sdk.onAuthStateChanged(api().auth, (u) => { if (u) seenUser = true; else if (seenUser) fn(); });
};

// ── Files ─────────────────────────────────────────────────────────────────────────────────────
// The upload queue (services/mediaQueue.js) and project files use these; they are the only
// file-transfer network calls in the app.

/** A signed URL for one store path. action 'write' needs contentType + size. */
export async function signStorage(path, action, contentType, size) {
  const r = await call('__storageSign', { path, action, contentType, size });
  return r?.url;
}

/** PUT a device file to a signed write URL. Throws on a non-2xx. */
export const putFile = (url, localUri, contentType) => platform().files.putFile(url, localUri, contentType);

/** Download a file (a synced project file seen on another device) into the app's cache (web: the URL itself). */
export const downloadToCache = (url, name) => platform().files.downloadToCache(url, name);
